import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Divider,
  Container,
  CircularProgress,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import NumberFormat from "react-number-format";
import InputBase from "@mui/material/InputBase";

import CustomInput from "../../components/common/customInput";
import colors from "../../../config/colors";
import * as apiService from "../../../api";

const pageStyle = {
  minHeight: "100vh",
  width: "100vw",
  backgroundColor: "#f5f5f5",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "20px",
};

const containerStyle = {
  backgroundColor: "white",
  borderRadius: "12px",
  padding: "2rem",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
  border: `2px solid ${colors.primary}`,
  width: "100%",
  maxWidth: "900px",
  maxHeight: "90vh",
  overflowY: "auto",
};

const labelStyles = {
  fontFamily: "Montserrat",
  fontWeight: "bold",
  fontSize: "14px",
  color: colors.primary,
  marginBottom: "8px",
};

const inputStyles = {
  boxShadow: "0 3px 10px rgb(0 0 0 / 0.2)",
  borderRadius: "10px",
  backgroundColor: "white",
  fontFamily: "Montserrat",
  padding: "12px 16px",
  fontSize: "14px",
  border: `1px solid ${colors.primary}`,
  width: "100%",
};

const steps = ["Teléfonos", "Dirección", "Correo Electrónico"];

// Estilos personalizados para el Stepper
const stepperStyles = {
  "& .MuiStepIcon-root": {
    color: "#e0e0e0", // Color para steps inactivos
    "&.Mui-active": {
      color: colors.primary, // Color primario para step activo
    },
    "&.Mui-completed": {
      color: colors.primary, // Color primario para steps completados
    },
  },
  "& .MuiStepIcon-text": {
    fill: "white", // Texto blanco dentro de las burbujas
  },
  "& .MuiStepConnector-alternativeLabel": {
    top: 10,
    left: "calc(-50% + 16px)",
    right: "calc(50% + 16px)",
  },
  "& .MuiStepConnector-alternativeLabel.Mui-active .MuiStepConnector-line": {
    borderColor: colors.primary,
  },
  "& .MuiStepConnector-alternativeLabel.Mui-completed .MuiStepConnector-line": {
    borderColor: colors.primary,
  },
};

const OnboardingPage = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [stepLoading, setStepLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [datosOriginales, setDatosOriginales] = useState(null);
  const [datosFormulario, setDatosFormulario] = useState({});
  const [shouldPopulateForm, setShouldPopulateForm] = useState(false);

  const [provincias, setProvincias] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [municipiosFiltrados, setMunicipiosFiltrados] = useState([]);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const infoAfiliado = useSelector((state) => state.afiliado.infoAfiliado);

  // Verificar si el afiliado es del régimen subsidiado
  const esRegimenSubsidiado = infoAfiliado?.regimen === "SUBSIDIADO";

  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm({ mode: "onChange" });

  // Watch para cambios en provincia
  const watchProvincia = watch("idProvincia");

  useEffect(() => {
    if (infoAfiliado.codigoAfiliado) {
      cargarDatosIniciales();
    } else {
      navigate("/login", { replace: true });
    }
  }, [infoAfiliado.codigoAfiliado, navigate]);

  // Efecto para filtrar municipios cuando cambia la provincia
  useEffect(() => {
    if (watchProvincia && municipios.length > 0) {
      const municipiosFiltrados = municipios.filter((m) => {
        const coincide = m.codigoPadre === Number(watchProvincia);
        return coincide;
      });

      setMunicipiosFiltrados(municipiosFiltrados);

      // Solo limpiar el municipio si no es la carga inicial o si el municipio actual no pertenece a la nueva provincia
      const municipioActual = watch("idMunicipio");
      const municipioPerteneceANuevaProvincia = municipiosFiltrados.some(
        (m) => m.codigo === Number(municipioActual)
      );

      if (!municipioPerteneceANuevaProvincia) {
        setValue("idMunicipio", "");
      }

      if (municipiosFiltrados.length === 0) {
        console.warn(
          "⚠️ No se encontraron municipios para la provincia:",
          watchProvincia
        );
      }
    }
  }, [watchProvincia, municipios, setValue, watch]);

  // Efecto adicional para manejar la carga inicial de municipios
  useEffect(() => {
    if (!isInitialLoad && municipios.length > 0) {
      if (watchProvincia) {
        // Este efecto se ejecuta después de la carga inicial para asegurar que los municipios estén filtrados correctamente
        const municipiosFiltrados = municipios.filter((m) => {
          const coincide = m.codigoPadre === Number(watchProvincia);
          return coincide;
        });

        setMunicipiosFiltrados(municipiosFiltrados);
      } else if (datosOriginales && datosOriginales.idMunicipio) {
        // Si no hay provincia seleccionada pero hay datos originales con municipio, establecer el municipio
        setValue("idMunicipio", datosOriginales.idMunicipio);
      }
    }
  }, [isInitialLoad, municipios, watchProvincia, datosOriginales, setValue]);

  // Efecto para poblar el formulario con datos guardados cuando se cambia de step
  useEffect(() => {
    if (
      shouldPopulateForm &&
      Object.keys(datosFormulario).length > 0 &&
      !isInitialLoad
    ) {
      // Poblar el formulario con los datos guardados del step actual
      setTimeout(() => {
        Object.keys(datosFormulario).forEach((key) => {
          if (
            datosFormulario[key] !== undefined &&
            datosFormulario[key] !== null
          ) {
            setValue(key, datosFormulario[key]);
          }
        });
        setShouldPopulateForm(false);
      }, 100);
    }
  }, [
    activeStep,
    shouldPopulateForm,
    datosFormulario,
    setValue,
    isInitialLoad,
  ]);

  // Efecto para inicializar datosFormulario con los datos originales
  useEffect(() => {
    if (datosOriginales && Object.keys(datosFormulario).length === 0) {
      const datosIniciales = {
        telefonoCelular: datosOriginales.telefonoCelular || "",
        telefonoResidencial: datosOriginales.telefonoResidencial || "",
        idProvincia: datosOriginales.idProvincia || "",
        idMunicipio: datosOriginales.idMunicipio || "",
        calle: datosOriginales.calle || "",
        noCasa: datosOriginales.noCasa || "",
        apartamento: datosOriginales.apartamento || "",
        edificio: datosOriginales.edificio || "",
        sector: datosOriginales.sector || "",
        correoElectronico: datosOriginales.correoElectronico || "",
      };
      setDatosFormulario(datosIniciales);
    }
  }, [datosOriginales, datosFormulario]);

  const mostrarMensaje = useCallback(
    (mensaje, tipo) => {
      enqueueSnackbar(mensaje, {
        variant: tipo,
        action: (key) => (
          <Button
            style={{ color: "white", fontWeight: "bold" }}
            onClick={() => closeSnackbar(key)}
          >
            X
          </Button>
        ),
      });
    },
    [enqueueSnackbar, closeSnackbar]
  );

  const cargarDatosIniciales = async () => {
    try {
      setInitialLoading(true);

      // Cargar las listas de drops y datos de contacto
      let provinciasData = [];
      let municipiosData = [];
      let datos = null;

      try {
        // Cargar provincias
        const provinciasRes = await apiService.drops.getAllProvincias();
        provinciasData = provinciasRes.data?.result || [];
      } catch (error) {
        console.error("❌ Error cargando provincias:", error);
        mostrarMensaje("Error cargando provincias", "warning");
      }

      try {
        // Cargar municipios
        const municipiosRes = await apiService.drops.getAllMunicipios();
        municipiosData = municipiosRes.data?.result || [];
      } catch (error) {
        console.error("❌ Error cargando municipios:", error);
        mostrarMensaje("Error cargando municipios", "warning");
      }

      try {
        // Cargar datos de contacto
        const datosContactoRes = await apiService.afiliado.getDatosContactos(
          infoAfiliado.codigoAfiliado
        );
        datos = datosContactoRes.data;
      } catch (error) {
        console.error("❌ Error cargando datos de contacto:", error);
        mostrarMensaje("Error cargando datos de contacto", "error");
      }

      // Establecer los datos cargados
      setProvincias(provinciasData);
      setMunicipios(municipiosData);

      // Poblar el formulario con los datos existentes
      if (datos) {
        // Teléfonos
        setValue("telefonoCelular", datos.telefonoCelular || "");
        setValue("telefonoResidencial", datos.telefonoResidencial || "");

        // Dirección - establecer provincia primero
        setValue("idProvincia", datos.idProvincia || "");
        setValue("calle", datos.calle || "");
        setValue("noCasa", datos.noCasa || "");
        setValue("apartamento", datos.apartamento || "");
        setValue("edificio", datos.edificio || "");
        setValue("sector", datos.sector || "");

        // Correo
        setValue("correoElectronico", datos.correoElectronico || "");

        setDatosOriginales(datos);

        // Si ya tiene provincia seleccionada, filtrar municipios inmediatamente
        if (datos.idProvincia && municipiosData.length > 0) {
          const municipiosFiltrados = municipiosData.filter((m) => {
            const codigoPadreNumerico = Number(m.codigoPadre);
            const provinciaNumerico = Number(datos.idProvincia);
            const coincide = codigoPadreNumerico === provinciaNumerico;

            return coincide;
          });

          setMunicipiosFiltrados(municipiosFiltrados);

          // Verificar que el municipio actual existe en los municipios filtrados
          if (datos.idMunicipio) {
            const municipioExiste = municipiosFiltrados.some(
              (m) => m.codigo === Number(datos.idMunicipio)
            );

            if (!municipioExiste) {
              console.warn(
                "⚠️ El municipio actual no pertenece a la provincia seleccionada:",
                {
                  municipioId: datos.idMunicipio,
                  provinciaId: datos.idProvincia,
                  municipiosDisponibles: municipiosFiltrados.map((m) => ({
                    id: m.codigo,
                    nombre: m.descripcion,
                  })),
                }
              );
              // Limpiar el municipio si no pertenece a la provincia
              setValue("idMunicipio", "");
            }
          }

          if (municipiosFiltrados.length === 0) {
            console.warn(
              "⚠️ PROBLEMA: No se encontraron municipios para la provincia inicial:",
              {
                provinciaId: datos.idProvincia,
                tipoProvinciId: typeof datos.idProvincia,
                municipiosTotales: municipiosData.length,
                ejemplosMunicipios: municipiosData.slice(0, 3).map((m) => ({
                  descripcion: m.descripcion,
                  codigoPadre: m.codigoPadre,
                  tipoCodigoPadre: typeof m.codigoPadre,
                })),
              }
            );
          } else {
            // Establecer el municipio después de filtrar
            setValue("idMunicipio", datos.idMunicipio || "");
          }
        }
      }
    } catch (error) {
      console.error("❌ Error general cargando datos iniciales:", error);
      mostrarMensaje("Error cargando datos iniciales", "error");
    } finally {
      setInitialLoading(false);
      setIsInitialLoad(false);
    }
  };

  const handleNext = () => {
    // Guardar los datos del step actual antes de avanzar
    const datosActuales = watch();

    if (!esRegimenSubsidiado && activeStep === 1) {
      if (!datosActuales.calle || 
          !datosActuales.idMunicipio || 
          !datosActuales.idProvincia || 
          !datosActuales.noCasa || 
          !datosActuales.sector
        ) {
          mostrarMensaje("Debe de llenar todos los campos", "warning")
        return
      } 
    }

    setDatosFormulario((prev) => ({
      ...prev,
      ...datosActuales,
    }));

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setShouldPopulateForm(true);
  };

  const handleBack = () => {
    // Guardar los datos del step actual antes de retroceder
    const datosActuales = watch();
    setDatosFormulario((prev) => ({
      ...prev,
      ...datosActuales,
    }));

    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setShouldPopulateForm(true);
  };

  const handleSkip = () => {
    // Redirigir al home sin guardar datos
    navigate("/", { replace: true });
  };

  const actualizarTelefonos = async (data) => {
    try {
      const telefonos = {
        TelefonoCelular: data.telefonoCelular || "",
        TelefonoResidencial: data.telefonoResidencial || "",
      };

      const response = await apiService.afiliado.postUpdateTelefonos(
        infoAfiliado.codigoAfiliado,
        telefonos
      );

      if (
        response &&
        response.data &&
        response.data.result &&
        response.data.result.codigoMensaje
      ) {
        mostrarMensaje("Teléfonos actualizados exitosamente", "success");
        return true;
      } else {
        console.error(
          "❌ El servidor no confirmó la actualización de teléfonos"
        );
        mostrarMensaje("Error actualizando teléfonos", "error");
        return false;
      }
    } catch (error) {
      console.error("❌ Error actualizando teléfonos:", error);
      mostrarMensaje("Error actualizando teléfonos", "error");
      return false;
    }
  };

  const verificarDatosActuales = async () => {
    try {
      const response = await apiService.afiliado.getDatosContactos(
        infoAfiliado.codigoAfiliado
      );
    } catch (error) {
      console.warn("⚠️ No se pudieron verificar los datos actuales:", error);
    }
  };

  const actualizarDireccion = async (data) => {
    try {
      // Verificar datos actuales antes de la actualización
      await verificarDatosActuales();

      // ✅ USAR EL FORMATO EXACTO QUE ESPERA EL ENDPOINT
      const direccion = {
        Provincia:
          provincias.find((p) => p.codigo === Number(data.idProvincia))
            ?.descripcion || "",
        IdProvincia: Number(data.idProvincia),
        Municipio:
          municipios.find((m) => m.codigo === Number(data.idMunicipio))
            ?.descripcion || "",
        IdMunicipio: Number(data.idMunicipio),
        Calle: data.calle || "",
        NoCasa: data.noCasa || "",
        Apartamento: data.apartamento || "",
        Edificio: data.edificio || "",
        Sector: data.sector || "",
      };

      const response = await apiService.afiliado.postUpdateDireccion(
        infoAfiliado.codigoAfiliado,
        direccion
      );

      // Verificar datos después de la actualización
      setTimeout(async () => {
        await verificarDatosActuales();
      }, 1000);

      // Verificar si la respuesta indica éxito
      if (
        response &&
        response.data &&
        response.data.result &&
        response.data.result.codigoMensaje
      ) {
        mostrarMensaje("Dirección actualizada exitosamente", "success");
        return true;
      } else {
        console.error(
          "❌ El servidor no confirmó la actualización de dirección"
        );
        mostrarMensaje("Error actualizando dirección", "error");
        return false;
      }
    } catch (error) {
      console.error("❌ Error actualizando dirección:", error);

      if (error.response) {
        const statusCode = error.response.status;
        const errorMessage =
          error.response.data?.message ||
          error.response.data?.error ||
          "Error del servidor";
        console.error(`❌ Error del servidor (${statusCode}):`, errorMessage);
        mostrarMensaje(
          `Error del servidor (${statusCode}): ${errorMessage}`,
          "error"
        );
      } else if (error.request) {
        console.error("❌ Sin respuesta del servidor");
        mostrarMensaje("Error: No se pudo conectar con el servidor", "error");
      } else {
        console.error("❌ Error de configuración:", error.message);
        mostrarMensaje(`Error: ${error.message}`, "error");
      }

      return false;
    }
  };

  const actualizarCorreo = async (data) => {
    try {
      const correo = {
        CorreoElectronico: data.correoElectronico,
      };

      const token = localStorage.getItem("token");
      if (!token) {
        console.error("❌ No se encontró token de autenticación");
        mostrarMensaje("Error: No se encontró token de autenticación", "error");
        return false;
      }

      const response = await apiService.afiliado.postUpdateCorreo(
        infoAfiliado.codigoAfiliado,
        correo,
        token
      );

      if (
        response &&
        response.data &&
        response.data.result &&
        response.data.result.codigoMensaje
      ) {
        mostrarMensaje(
          "Correo electrónico actualizado exitosamente",
          "success"
        );
        return true;
      } else {
        console.error("❌ El servidor no confirmó la actualización de correo");
        mostrarMensaje("Error actualizando correo electrónico", "error");
        return false;
      }
    } catch (error) {
      console.error("❌ Error actualizando correo:", error);

      if (error.response) {
        const statusCode = error.response.status;
        const errorMessage =
          error.response.data?.message || "Error del servidor";
        console.error(`❌ Error del servidor (${statusCode}):`, errorMessage);
        mostrarMensaje(`Error del servidor: ${errorMessage}`, "error");
      } else if (error.request) {
        console.error("❌ Sin respuesta del servidor");
        mostrarMensaje("Error: No se pudo conectar con el servidor", "error");
      } else {
        console.error("❌ Error de configuración:", error.message);
        mostrarMensaje(`Error: ${error.message}`, "error");
      }

      return false;
    }
  };

  const guardarTodosLosDatos = async (datosCompletos) => {
    try {
      setStepLoading(true);
      let errores = [];

      // 1. Actualizar teléfonos
      try {
        const telefonos = {
          TelefonoCelular: datosCompletos.telefonoCelular || "",
          TelefonoResidencial: datosCompletos.telefonoResidencial || "",
        };

        const responseTelefonos = await apiService.afiliado.postUpdateTelefonos(
          infoAfiliado.codigoAfiliado,
          telefonos
        );

        if (!responseTelefonos?.data?.result?.codigoMensaje) {
          errores.push("teléfonos");
        }
      } catch (error) {
        console.error("❌ Error actualizando teléfonos:", error);
        errores.push("teléfonos");
      }

      // 2. Actualizar dirección (solo si no es régimen subsidiado)
      if (!esRegimenSubsidiado) {
        try {
          const direccion = {
            Provincia:
              provincias.find(
                (p) => p.codigo === Number(datosCompletos.idProvincia)
              )?.descripcion || "",
            IdProvincia: Number(datosCompletos.idProvincia),
            Municipio:
              municipios.find(
                (m) => m.codigo === Number(datosCompletos.idMunicipio)
              )?.descripcion || "",
            IdMunicipio: Number(datosCompletos.idMunicipio),
            Calle: datosCompletos.calle || "",
            NoCasa: datosCompletos.noCasa || "",
            Apartamento: datosCompletos.apartamento || "",
            Edificio: datosCompletos.edificio || "",
            Sector: datosCompletos.sector || "",
          };

          const responseDireccion =
            await apiService.afiliado.postUpdateDireccion(
              infoAfiliado.codigoAfiliado,
              direccion
            );

          if (!responseDireccion?.data?.result?.codigoMensaje) {
            errores.push("dirección");
          }
        } catch (error) {
          console.error("❌ Error actualizando dirección:", error);
          errores.push("dirección");
        }
      }

      // 3. Actualizar correo
      try {
        const correo = {
          CorreoElectronico: datosCompletos.correoElectronico,
        };

        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("No se encontró token de autenticación");
        }

        const responseCorreo = await apiService.afiliado.postUpdateCorreo(
          infoAfiliado.codigoAfiliado,
          correo,
          token
        );

        if (!responseCorreo?.data?.result?.codigoMensaje) {
          errores.push("correo electrónico");
        }
      } catch (error) {
        console.error("❌ Error actualizando correo:", error);
        errores.push("correo electrónico");
      }

      // Verificar resultados
      if (errores.length === 0) {
        mostrarMensaje(
          "¡Todos los datos de contacto han sido actualizados exitosamente!",
          "success"
        );
        return true;
      } else {
        const erroresTexto = errores.join(", ");
        mostrarMensaje(
          `Error actualizando: ${erroresTexto}. Por favor, inténtelo nuevamente.`,
          "error"
        );
        return false;
      }
    } catch (error) {
      console.error("❌ Error general guardando datos:", error);
      mostrarMensaje(
        "Ocurrió un error inesperado al guardar los datos. Inténtelo nuevamente.",
        "error"
      );
      return false;
    } finally {
      setStepLoading(false);
    }
  };

  const onSubmit = async (data) => {
    setStepLoading(true);

    try {
      // Guardar los datos del step actual en el estado
      setDatosFormulario((prev) => ({
        ...prev,
        ...data,
      }));

      if (activeStep === 0) {
        // Paso 1: Validar teléfonos
        if (!data.telefonoCelular && !data.telefonoResidencial) {
          mostrarMensaje(
            "Por favor ingrese al menos un número de teléfono.",
            "warning"
          );
          setStepLoading(false);
          return;
        }
        handleNext();
      } else if (activeStep === 1) {
        // Paso 2: Validar dirección (solo si no es régimen subsidiado)
        if (!esRegimenSubsidiado && (!data.idProvincia || !data.idMunicipio)) {
          mostrarMensaje(
            "Por favor complete todos los campos obligatorios de ubicación.",
            "warning"
          );
          setStepLoading(false);
          return;
        }
        handleNext();
      } else if (activeStep === 2) {
        // Paso 3: Validar correo electrónico
        if (!data.correoElectronico || data.correoElectronico.trim() === "") {
          mostrarMensaje(
            "Por favor ingrese un correo electrónico válido.",
            "warning"
          );
          setStepLoading(false);
          return;
        }

        // En el último paso, guardar todos los datos
        const datosCompletos = {
          ...datosFormulario,
          ...data,
        };

        const exito = await guardarTodosLosDatos(datosCompletos);

        if (exito) {
          // Redirigir después de un breve delay
          setTimeout(() => {
            navigate("/", { replace: true });
          }, 2000);
        }
      }
    } catch (error) {
      console.error("❌ Error en onSubmit:", error);
      mostrarMensaje(
        "Ocurrió un error inesperado. Inténtelo nuevamente.",
        "error"
      );
    } finally {
      setStepLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Actualice sus teléfonos
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Mantenga sus números de contacto actualizados para una mejor
                comunicación.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomInput
                label="Teléfono Celular"
                labelStyle={labelStyles}
                style={inputStyles}
                placeholder="(000) 000-0000"
                registerForm={register("telefonoCelular", {
                  pattern: {
                    value: /^[\d\(\)\s\-\+]+$/,
                    message: "Formato de teléfono inválido",
                  },
                })}
              />
              {errors.telefonoCelular && (
                <Typography color="error" variant="caption">
                  {errors.telefonoCelular.message}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomInput
                label="Teléfono Residencial"
                labelStyle={labelStyles}
                style={inputStyles}
                placeholder="(000) 000-0000"
                registerForm={register("telefonoResidencial", {
                  pattern: {
                    value: /^[\d\(\)\s\-\+]+$/,
                    message: "Formato de teléfono inválido",
                  },
                })}
              />
              {errors.telefonoResidencial && (
                <Typography color="error" variant="caption">
                  {errors.telefonoResidencial.message}
                </Typography>
              )}
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {esRegimenSubsidiado
                  ? "Su dirección"
                  : "Actualice su dirección"}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {esRegimenSubsidiado
                  ? "Su dirección está registrada en el sistema y no puede ser modificada."
                  : "Complete su dirección para mejorar la calidad del servicio."}
              </Typography>
              {esRegimenSubsidiado && (
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "#fff3cd",
                    borderRadius: 1,
                    border: "1px solid #ffeaa7",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    style={{ color: "#856404", fontFamily: "Montserrat" }}
                  >
                    ℹ️ Los afiliados del régimen subsidiado no pueden modificar
                    su dirección desde esta plataforma.
                  </Typography>
                </Box>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel
                  style={{
                    color: colors.primary,
                    fontFamily: "Montserrat",
                    backgroundColor: "white",
                    padding: "0 4px",
                    marginLeft: "-4px",
                  }}
                  shrink={!!watch("idProvincia")}
                >
                  Provincia {!esRegimenSubsidiado && "*"}
                </InputLabel>
                <Controller
                  name="idProvincia"
                  control={control}
                  rules={
                    !esRegimenSubsidiado
                      ? { required: "La provincia es obligatoria" }
                      : {}
                  }
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value || ""}
                      disabled={esRegimenSubsidiado}
                    >
                      {provincias.map((provincia) => (
                        <MenuItem
                          key={provincia.codigo}
                          value={provincia.codigo}
                        >
                          {provincia.descripcion}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
              {!esRegimenSubsidiado && errors.idProvincia && (
                <Typography color="error" variant="caption">
                  {errors.idProvincia.message}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel
                  style={{
                    color: colors.primary,
                    fontFamily: "Montserrat",
                    backgroundColor: "white",
                    padding: "0 4px",
                    marginLeft: "-4px",
                  }}
                  shrink={!!watch("idMunicipio")}
                >
                  Municipio {!esRegimenSubsidiado && "*"}
                </InputLabel>
                <Controller
                  name="idMunicipio"
                  control={control}
                  rules={
                    !esRegimenSubsidiado
                      ? { required: "El municipio es obligatorio" }
                      : {}
                  }
                  render={({ field }) => {
                    return (
                      <Select
                        {...field}
                        value={field.value || ""}
                        disabled={esRegimenSubsidiado || !watchProvincia}
                      >
                        {municipiosFiltrados.map((municipio) => {
                          return (
                            <MenuItem
                              key={municipio.codigo}
                              value={municipio.codigo}
                            >
                              {municipio.descripcion}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    );
                  }}
                />
              </FormControl>
              {!esRegimenSubsidiado && errors.idMunicipio && (
                <Typography color="error" variant="caption">
                  {errors.idMunicipio.message}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomInput
                label="Calle"
                labelStyle={labelStyles}
                style={{
                  ...inputStyles,
                  backgroundColor: esRegimenSubsidiado ? "#f5f5f5" : "white",
                  color: esRegimenSubsidiado ? "#666" : "inherit",
                }}
                placeholder="Nombre de la calle"
                registerForm={register("calle")}
                disabled={esRegimenSubsidiado}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomInput
                label="No. de Casa"
                labelStyle={labelStyles}
                style={{
                  ...inputStyles,
                  backgroundColor: esRegimenSubsidiado ? "#f5f5f5" : "white",
                  color: esRegimenSubsidiado ? "#666" : "inherit",
                }}
                placeholder="Número de casa"
                registerForm={register("noCasa")}
                disabled={esRegimenSubsidiado}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <CustomInput
                label="Apartamento"
                labelStyle={labelStyles}
                style={{
                  ...inputStyles,
                  backgroundColor: esRegimenSubsidiado ? "#f5f5f5" : "white",
                  color: esRegimenSubsidiado ? "#666" : "inherit",
                }}
                placeholder="Número de apartamento"
                registerForm={register("apartamento")}
                disabled={esRegimenSubsidiado}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <CustomInput
                label="Edificio"
                labelStyle={labelStyles}
                style={{
                  ...inputStyles,
                  backgroundColor: esRegimenSubsidiado ? "#f5f5f5" : "white",
                  color: esRegimenSubsidiado ? "#666" : "inherit",
                }}
                placeholder="Nombre del edificio"
                registerForm={register("edificio")}
                disabled={esRegimenSubsidiado}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <CustomInput
                label="Sector"
                labelStyle={labelStyles}
                style={{
                  ...inputStyles,
                  backgroundColor: esRegimenSubsidiado ? "#f5f5f5" : "white",
                  color: esRegimenSubsidiado ? "#666" : "inherit",
                }}
                placeholder="Sector o zona"
                registerForm={register("sector")}
                disabled={esRegimenSubsidiado}
              />
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Actualice su correo electrónico
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Su correo electrónico es importante para recibir notificaciones
                y comunicaciones oficiales.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <CustomInput
                label="Correo Electrónico *"
                labelStyle={labelStyles}
                style={inputStyles}
                placeholder="correo@ejemplo.com"
                type="email"
                registerForm={register("correoElectronico", {
                  required: "El correo electrónico es obligatorio",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Ingrese un correo electrónico válido",
                  },
                })}
              />
              {errors.correoElectronico && (
                <Typography color="error" variant="caption">
                  {errors.correoElectronico.message}
                </Typography>
              )}
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  // Log de debugging
  if (initialLoading) {
    return (
      <Box style={pageStyle}>
        <Box style={containerStyle}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "400px",
            }}
          >
            <CircularProgress size={60} sx={{ color: colors.primary, mb: 2 }} />
            <Typography
              variant="h6"
              style={{
                fontFamily: "Montserrat",
                color: colors.primary,
                textAlign: "center",
              }}
            >
              Cargando información...
            </Typography>
            <Typography
              variant="body2"
              style={{
                fontFamily: "Montserrat",
                color: "#666",
                textAlign: "center",
                marginTop: "8px",
              }}
            >
              Preparando su proceso de actualización de datos
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box style={pageStyle}>
      <Box style={containerStyle}>
        {activeStep === 0 && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button
              onClick={handleSkip}
              variant="text"
              sx={{
                fontFamily: "Montserrat",
                fontWeight: "normal",
                fontSize: "14px",
                color: "#999",
                textDecoration: "underline",
                "&:hover": {
                  color: colors.primary,
                  backgroundColor: "transparent",
                  textDecoration: "underline",
                },
                textTransform: "none",
              }}
            >
              Omitir por ahora
            </Button>
          </Box>
        )}

        <Typography
          variant="h4"
          style={{
            fontFamily: "Montserrat",
            fontWeight: "bold",
            textAlign: "center",
            marginBottom: "16px",
            color: colors.primary,
          }}
        >
          Actualización de Datos de Contacto
        </Typography>

        <Typography
          variant="h6"
          style={{
            fontFamily: "Montserrat",
            textAlign: "center",
            marginBottom: "32px",
            color: "#666",
          }}
        >
          Complete todos los pasos para actualizar sus datos de contacto. Los
          datos se guardarán al final del proceso.
        </Typography>

        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{ mb: 4, ...stepperStyles }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {renderStepContent(activeStep)}

            <Divider sx={{ my: 3 }} />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                sx={{
                  fontFamily: "Montserrat",
                  fontWeight: "bold",
                  color: colors.primary,
                }}
              >
                Anterior
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={stepLoading}
                sx={{
                  fontFamily: "Montserrat",
                  fontWeight: "bold",
                  backgroundColor: colors.primary,
                  "&:hover": {
                    backgroundColor: colors.primary,
                    opacity: 0.9,
                  },
                  minWidth: "120px",
                }}
              >
                {stepLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : activeStep === steps.length - 1 ? (
                  "Confirmar"
                ) : (
                  "Siguiente"
                )}
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>
    </Box>
  );
};

export default OnboardingPage;
